import {
  DoubleSide,
  FrontSide,
  LineBasicMaterial,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PointsMaterial,
  Side,
  Uint16BufferAttribute,
  Vector2,
  Vector3,
} from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import _ from 'underscore';

import {
  CellGridVolumeModel,
  DynamicAssetType,
  PrismVolumeModel,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { userStateAdapter } from '../../react/state/user-state/user-state-adapter';
import { TokenService } from '../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../services/network/file-upload';
import { WebApi } from '../../services/web-api';
import { gzip } from '../../utils/compressionUtils';
import { localStorageUtils } from '../../utils/local-storage-utils';
import { isDefined } from '../../utils/variables';
import { CoordinateManager } from '../coordinate-manager';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { Point2d } from '../utilities/math-helpers';
import { heightLabelUtils } from './components/height-labels';
import { DesignNode, DesignNodeMaterials } from './design/design-node/design-node';
import { getHiddenSeams } from './design/design-node/utils/circle';
import { DesignMetaData, summarizeMetadata } from './design/design-node/utils/meta-data';
import {
  generateDesignNodes,
  GenerateDesignNodesResult,
  serializeDesignNodeToGlb,
} from './design/serialize';
import { ColorCoding } from './design/skymap-extras';
import { AssetLoadingStatus, AssetType, ElementObj } from './element-obj';
import { HasVolumes } from './interfaces/has-cell-grid-volumes';
import { HasOpacity } from './interfaces/has-opacity';
import { CellGridVolume } from './volume/cell-grid-volume';
import { PrismVolume } from './volume/prism-volume';
import { VolumeHandler } from './volume/volume-handler';

interface DesignOptions {
  assetId?: string;
  name: string;
  visible: boolean;
  wgs84?: {
    lng: number | string;
    lat: number | string;
  };
  MAS: number | undefined;
  cellGridVolumes?: CellGridVolumeModel[];
  prismVolumes?: PrismVolumeModel[];
  skyMapVolumeSourceIds?: string[];
  sharedViewId?: string;
  timelineContainerId?: string;
  assetTagIds?: string[];
}

const isEditedPointSize = 8;
const isIdlePointSize = 6;
function getPointSize(editing: boolean, multiplier?: number) {
  return (
    (editing ? isEditedPointSize : isIdlePointSize) *
    (multiplier ??
      localStorageUtils.getNumericUserItemOrDefault('designToolPointSizeMultiplier', 1))
  );
}

export class Design extends ElementObj implements HasOpacity, HasVolumes {
  objType: DynamicAssetType = 'Design';
  get assetType(): AssetType {
    return 'design';
  }

  metaData: DesignMetaData = {
    triangleCount: 0,
    area: 0,
    planarArea: 0,
    lineLength: 0,
    lineSegmentCount: 0,
    linePlanarLength: 0,
    pointCount: 0,
  };
  onDoubleSideChanged?: (side: Side) => void;
  onVisibilityChanged?: () => void;

  private _enabled = true;
  private _opacity = 1;
  private sharedViewId?: string;

  get enabled() {
    return this._enabled;
  }

  get side(): Side {
    return this._side;
  }
  private _side: Side = DoubleSide;

  get opacity() {
    return this._opacity;
  }

  set opacity(opacity: number) {
    this._opacity = opacity;

    const refreshMaterial = (
      material: MeshStandardMaterial | MeshBasicMaterial | LineBasicMaterial | PointsMaterial,
    ) => {
      material.opacity = opacity;
      material.transparent = opacity < 1;
      material.needsUpdate = true;
    };

    refreshMaterial(this.materials.points);
    refreshMaterial(this.materials.lineSegments);
    refreshMaterial(this.materials.faces);
    refreshMaterial(this.materials.texts);
  }

  materials: DesignNodeMaterials = {
    faces: new MeshStandardMaterial({
      transparent: false,
      flatShading: true,
      side: DoubleSide,
      vertexColors: true,
    }),
    lineSegments: new LineBasicMaterial({
      transparent: false,
      vertexColors: true,
    }),
    points: new PointsMaterial({
      transparent: false,
      opacity: 1.0,
      size: getPointSize(false),
      sizeAttenuation: false,
      vertexColors: true,
    }),
    texts: new MeshBasicMaterial({
      transparent: false,
      opacity: 1.0,
      side: DoubleSide,
    }),
  };
  designNode: DesignNode;

  cellGridVolumes: CellGridVolume[] = [];
  prismVolumes: PrismVolume[] = [];
  skyMapVolumeSourceIds: string[] = [];
  volumeHandler = new VolumeHandler(this);

  setPointSize(locked: boolean, multiplier?: number) {
    this.materials.points.size = getPointSize(locked, multiplier);
    this.materials.points.needsUpdate = true;
  }

  toggleDoubleSided(newValue?: Side) {
    const side = newValue
      ? newValue
      : this.materials.faces.side === DoubleSide
        ? FrontSide
        : DoubleSide;

    this.materials.faces.side = side;
    this._side = side;
    this.materials.faces.needsUpdate = true;
    publish(SubscriptionTopic.DesignDoubleSideChanged, undefined);
  }

  /**
   * Hook for catching changes to metadata in UI
   */
  onMetadataChanged?: () => void;

  /**
   * Hook for catching changes to locked state in UI
   */
  onLockedChanged?: (value: boolean) => void;
  get locked() {
    return this._locked;
  }
  set locked(value: boolean) {
    this.onLockedChanged?.(value);
    this._locked = value;
  }
  private _locked = false;

  /**
   * In-flight rotation/geometry save loop, if any. Used to keep saves single-flight so an
   * older upload can never commit its uploadId after a newer one.
   */
  private geometrySavePromise?: Promise<void>;

  /**
   * Set whenever a rotation save is requested. The in-flight loop consumes it, so rapid
   * rotations coalesce into a single trailing save with the latest state.
   */
  private geometrySaveRequested = false;

  constructor(options: DesignOptions) {
    super(options.timelineContainerId!);

    // This will only allow downloading the points and linesegment points.
    this.downloadable = true;
    this.MAS = options.MAS;
    this.designNode = new DesignNode([], {
      materials: this.materials,
    });

    const hasDesignToolAccess = userStateAdapter
      .getSnapshot()
      .hasDesignToolAccessForCurrentProject();
    this.freeze = {
      translate: !hasDesignToolAccess,
      rotateZ: !hasDesignToolAccess,
      rotateXY: true,
      scale: true,
    };

    this.assetId = options?.assetId;
    this.name = options.name;
    this.sharedViewId = options?.sharedViewId;

    this.wgs84 = this.initPosition(options.wgs84);
    this.add(this.designNode.group);

    this.cellGridVolumes = CellGridVolume.fromPropertyArray(options.cellGridVolumes);
    this.prismVolumes = PrismVolume.fromPropertyArray(options.prismVolumes);
    this.skyMapVolumeSourceIds = options.skyMapVolumeSourceIds ?? [];
    this.volumeHandler.activeVolume = this.cellGridVolumes[0] ?? this.prismVolumes[0];

    this.calculateLocalPosition();

    void this.setStateVisibility(options.visible === true);
    this._assetTagIds = options.assetTagIds ?? [];
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'idle';
  }

  setColorCoding(colorCoding: ColorCoding) {
    if (isDefined(colorCoding.attributeId)) {
      const attribute = this.designNode.getAttribute(colorCoding.attributeId);
      this.designNode.setColorCoding(
        colorCoding,
        attribute.type === 'enum' ? attribute.colorMap : undefined,
      );
    } else {
      this.designNode.setColorCoding(colorCoding, undefined);
    }

    this.designNode.colorCoding = colorCoding;
    this.designNode.refresh();
  }

  getStateVisibility(): boolean {
    return super.getStateVisibility() && this.loadingStatus !== 'loading';
  }

  refreshMetadata() {
    this.metaData = summarizeMetadata(this.designNode, {
      points: this.designNode.points,
      lineSegments: this.designNode.lineSegments,
      faces: this.designNode.faces,
    });

    this.onMetadataChanged?.();

    if (this.labelsGroup) {
      heightLabelUtils.resetLabelGroup(this);
      this.refreshLabelsGroup();
    }
  }

  async saveGeometryToS3() {
    this.refreshMetadata();

    // Gzipped for a faster upload. The backend recognises it and stores it as is, with
    // Content-Encoding gzip, so the browser decodes it again while downloading.
    const data = await gzip(this.serializeToGlb());

    const upload = new FileUpload({
      apiBaseUrl: WebApi.getUrl(),
      authToken: new TokenService().get(),
      file: new File([data], 'geometry.glb'),
      concurrency: 1,
    });

    const uploadResult = await upload.start();
    const newAsset = this.assetId === undefined;

    if (newAsset) {
      // A brand new asset needs its id from the metadata save before the geometry can
      // reference the upload, so these must run in order.
      await this.save();
      await SkyMapAxiosServiceFactory.instance
        .createDynamicAssetServiceV0()
        .createSkyMapInformationModel({
          path: { id: this.assetId! },
          body: {
            uploadId: uploadResult.id,
          },
        });
    } else {
      // Commit the position (metadata) and the geometry only once the slow file upload has
      // finished, and fire both at the same time. A reload during the upload keeps the old
      // consistent state; the only remaining window is these two parallel, fast requests.
      await Promise.all([
        this.save(),
        SkyMapAxiosServiceFactory.instance
          .createDynamicAssetServiceV0()
          .updateSkyMapInformationModel({
            path: { id: this.assetId! },
            body: {
              uploadId: uploadResult.id,
            },
          }),
      ]);
    }

    this.loadingStatus = 'loaded';
  }

  onSaved(data?: any) {
    if (!data) {
      return;
    }

    this.assetId = data.id || this.assetId;
    this.timelineContainerId = data.timelineContainerId;
  }

  async onChange() {
    // The rotation is persisted inside the geometry GLB while the position is persisted as
    // metadata - two separate, non-atomic server writes. If the position (a fast request) is
    // committed before the slow geometry upload finishes, a reload in between leaves the
    // server with the new position but a stale rotation (the asset "jumps" on reload).
    //
    // So when the rotation changed, upload the geometry first and only then commit the
    // position and the geometry (together, see saveGeometryToS3). Skipping the normal save
    // here is what prevents the position from being committed before the upload.
    if (
      this.autoSave &&
      isDefined(this.assetId) &&
      !this.rotation.equals(this.designNode.rotation)
    ) {
      await this.saveRotation();
      publish(SubscriptionTopic.AssetChanged, this);
      return;
    }

    await super.onChange();
  }

  /**
   * Persists a rotation change to the geometry.
   *
   * Rotation saves are made single-flight and coalesced: while a save is running, further
   * requests only set a flag, and the in-flight loop picks up the latest state when it comes
   * around again. This guarantees uploads/commits never overlap (so an older upload can't
   * commit its uploadId after a newer one and leave the persisted geometry stale) and that
   * rapid rotations collapse into one trailing save instead of one upload each.
   *
   * The saved-rotation baseline (designNode.rotation) is only advanced once the matching save
   * has succeeded, so a failed save is retried on the next change rather than being skipped.
   */
  private saveRotation(): Promise<void> {
    this.geometrySaveRequested = true;

    if (!this.geometrySavePromise) {
      this.geometrySavePromise = this.runGeometrySaveLoop().finally(() => {
        this.geometrySavePromise = undefined;
      });
    }

    return this.geometrySavePromise;
  }

  private async runGeometrySaveLoop(): Promise<void> {
    while (this.geometrySaveRequested) {
      this.geometrySaveRequested = false;

      // Snapshot the rotation being saved. serializeToGlb (inside saveGeometryToS3) reads
      // this.quaternion synchronously before its first await, so the snapshot matches exactly
      // what gets uploaded even if the user keeps rotating during the upload.
      const rotationSnapshot = this.rotation.clone();

      await this.saveGeometryToS3();

      // Only advance the baseline after the matching save succeeded. If saveGeometryToS3
      // throws, the baseline stays put so the next onChange retries this rotation.
      this.designNode.rotation = rotationSnapshot;
    }
  }

  /**
   * Gets the matrix from the scene of the design's glb to local coordinates, as it is shown, for
   * the backend to draw it in the CAD drawing report. The glb's root node holds the group's
   * position and the design's rotation, see serializeToGlb.
   */
  getSceneToLocalMatrix(): Matrix4 {
    const groupPosition = this.designNode.group.position;
    const rootNode = new Matrix4().compose(groupPosition, this.quaternion, new Vector3(1, 1, 1));

    this.updateWorldMatrix(true, false);
    return this.matrixWorld
      .clone()
      .multiply(new Matrix4().makeTranslation(groupPosition))
      .multiply(rootNode.invert());
  }

  private serializeToGlb() {
    return serializeDesignNodeToGlb(
      this.designNode,
      this.designNode.attributes,
      this.designNode.colorCoding,
      {
        quaternion: this.quaternion,
        skipColors: true,
        groups: this.designNode.attributeGroups,
      },
    );
  }

  getSaveData() {
    this.MAS = this.position.z + (CoordinateManager.getInstance().getMAS() ?? 0);
    const saveData: any = {
      type: this.objType,
      properties: {
        name: this.name,
        visible: this.getStateVisibility(),
        MAS: this.MAS,
      },
      timelineContainerId: this.timelineContainerId,
    };

    if (this.assetId) {
      saveData.id = this.assetId;
    }

    const wgs84 = this.calculateWGS84Coords(new Vector2(this.position.x, this.position.y));
    if (wgs84) {
      this.wgs84 = {
        lng: wgs84.x,
        lat: wgs84.y,
      };

      saveData.properties.wgs84 = {
        lng: this.wgs84.lng.toString(),
        lat: this.wgs84.lat.toString(),
      };
    }

    return Promise.resolve(saveData);
  }

  /**
   * Incremenets all indices that are greater or equal to the startIndex
   * This is necessary when a new vertex in inserted or removed in the middle of the geometry
   * since it affects the index value of all the points after.
   */
  incrementIndices(
    bufferAttribute: Uint16BufferAttribute,
    startIndex: number,
    incrementValue: number,
  ) {
    for (let i = 0; i < bufferAttribute.count; i++) {
      const value = bufferAttribute.getX(i);
      if (value >= startIndex) {
        bufferAttribute.setX(i, value + incrementValue);
      }
    }
  }

  async onStateVisibilityChanged() {
    if (
      this.assetId &&
      this.visible &&
      this.loadingStatus !== 'loaded' &&
      this.loadingStatus !== 'loading'
    ) {
      await this.showModel();
    }
    await super.onStateVisibilityChanged();
    this.onVisibilityChanged?.();
  }

  async showModel() {
    try {
      this.loadingStatus = 'loading';
      await this.downloadAndLoadModel();
      this.loadingStatus = 'loaded';
    } catch (err) {
      this.visible = false;
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
      publish(SubscriptionTopic.SkyviewAssetVisibilityChanged, {
        assetUuid: this.uuid,
        visible: this.visible,
      });
    }
  }

  refreshLabelsGroup() {
    if (!this.labelsGroup) {
      return;
    }

    if (this.labelsSettings?.showHeightLabels) {
      // The seams of circles are not shown, see circle.ts.
      const hiddenSeams = getHiddenSeams(this.designNode);

      for (const point of this.designNode.pointNodes) {
        if (point.faces.length === 0 && !hiddenSeams.has(point.id)) {
          heightLabelUtils.createLabel(
            this.labelsGroup,
            point.vertex,
            this.labelsSettings.color,
            this.labelsSettings.fontSize,
            this.labelsSettings.sizeAttenuation ?? true,
            this.labelsSettings?.decimalPlaces,
          );
        }
      }
    }

    if (this.labelsSettings?.showLengthLabels) {
      for (const lineSegmentId of this.designNode.lineSegments) {
        const lineSegment = this.designNode.getLineSegmentById(lineSegmentId);
        heightLabelUtils.createLengthLabel(
          this.labelsGroup,
          this.designNode.getVertexByPointId(lineSegment.values[0], 'local'),
          this.designNode.getVertexByPointId(lineSegment.values[1], 'local'),
          this.labelsSettings.color,
          this.labelsSettings.fontSize,
          this.labelsSettings.sizeAttenuation ?? true,
          this.labelsSettings?.decimalPlaces,
        );
      }
    }

    return;
  }

  async downloadAndLoadModel() {
    const data = await this.loadSkyMapInformationModel(undefined, this.sharedViewId);
    this.loadGltf(data);
    this.loadingStatus = 'loaded';
  }

  loadGltf(data: GLTF) {
    this.loadingStatus = 'loaded';
    this.designNode.dispose();

    const result = generateDesignNodes(data.scene, {
      materials: this.materials,
      extractRootRotation: true,
    });
    if (!isDefined(result.nodes.length !== 1)) {
      throw new Error(`Unable to load design. ${result.nodes.length} nodes found.`);
    }

    this.designNode = result.nodes[0];
    this.rotation.copy(this.designNode.rotation);
    this.add(this.designNode.group);

    this.setColorCoding(this.designNode.colorCoding);
    publish(SubscriptionTopic.DesignAttributesChanged, undefined);

    this.designNode.refresh();
    this.refreshMetadata();
  }

  initPosition(wgs84?: { lng: number | string; lat: number | string }): {
    lng: number;
    lat: number;
  } {
    if (wgs84) {
      return { lng: Number(wgs84.lng), lat: Number(wgs84.lat) };
    }

    const wgs84Coords = this.calculateWGS84Coords(new Vector2(0, 0));

    return { lng: wgs84Coords?.x ?? 0, lat: wgs84Coords?.y ?? 0 };
  }

  onSelect() {
    super.onSelect();

    if (this.loadingStatus !== 'loaded' && this.loadingStatus !== 'loading' && this.assetId) {
      void this.showModel();
    }
  }

  onDeselect() {
    super.onDeselect();
  }

  getDesignNodeData(): GenerateDesignNodesResult {
    return {
      nodes: [this.designNode],
      transformationMatrix: this.matrix.clone(),
    };
  }

  getNativeAssetData() {
    const nativeGeomData = {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
      distance: 0,
      planeDistance: 0,
      planeArea: 0,
    };

    return nativeGeomData;
  }

  extractMeshInformation(mesh?: Mesh): { vertices: number[]; indices: number[] } {
    return {
      vertices: [],
      indices: [],
    };
  }

  calculateLocalPosition(): Point2d | undefined {
    const localPosition = super.calculateLocalPosition();
    this.position.set(
      localPosition?.x ?? 0,
      localPosition?.y ?? 0,
      this.calculateLocalHeight(this.MAS ?? 0) ?? 0,
    );
    return;
  }

  equals(design: Design) {
    const sameId = design.assetId === this.assetId;
    const sameVolumeSourceIds = _.isEqual(
      this.skyMapVolumeSourceIds.sort(),
      design.skyMapVolumeSourceIds.sort(),
    );

    return sameId && sameVolumeSourceIds;
  }
}
